import React from 'react'
import { Virtuoso } from 'react-virtuoso'
import { Loader, Modal, PrimaryButton, SecondaryButton, TextBox } from 'components'

type Option = { text: string; value: string }
export type MultiSelectWithModalProps = {
  id: string
  placeholder: string
  label?: string
  buttonText: string
  buttonBelow?: boolean
  modalTitle: string
  disabled?: boolean
  isLoading?: boolean
  value: string[] | { value: string[]; text: string[] }
  onChange(value: string[], text: string[], isDefaultAll?: boolean): void
  onOpen?: () => void
  options: Option[]
  error?: boolean
  helperText?: string
}

export const MultiSelectWithModal = (props: MultiSelectWithModalProps) => {
  const {
    id,
    placeholder,
    label,
    buttonText,
    buttonBelow,
    modalTitle,
    disabled,
    isLoading,
    value: innerValue,
    onChange,
    onOpen,
    options,
    error,
    helperText,
  } = props

  const { value, text } = React.useMemo(() => {
    return Array.isArray(innerValue)
      ? { value: [...innerValue], text: [...innerValue] }
      : { value: [...innerValue.value], text: [...innerValue.text] }
  }, [innerValue])

  const [open, setOpen] = React.useState<boolean>(false)
  const [innerState, setInnerState] = React.useState<{
    value: string[]
    text: string[]
    isDefaultAll?: boolean
  }>({ value, text })
  const [searchText, setSearchText] = React.useState<string>('')

  //Default select all options if inner state has 'all' only
  React.useEffect(() => {
    if (innerState['value'].length === 1 && innerState['value'][0] === 'all' && options.length > 0) {
      handleSelectAll(true, true)
    }
    // eslint-disable-next-line
  }, [innerState['value'], options])

  const handleOpen = () => {
    setInnerState({ value, text })
    setOpen(true)
    onOpen && onOpen()
  }

  const handleClose = () => {
    setSearchText('')
    setOpen(false)
  }

  const handleApply = () => {
    onChange(innerState['value'], innerState['text'], innerState['isDefaultAll'])
    handleClose()
  }

  const isSelected = (item: Option): boolean => {
    if (item.value === 'all') {
      return innerState['value'].includes('all')
    }
    return innerState['value'].includes(item.value)
  }

  const handleSelect = (event: React.ChangeEvent<HTMLInputElement>, selectedItem: { text: string; value: string }) => {
    if (event.target.name === 'all') {
      if (event.target.checked) {
        handleSelectAll(true)
      } else {
        handleSelectAll(false)
      }
    } else {
      let newValue = innerState['value']
      if (innerState['value'].includes(selectedItem['value'])) {
        newValue = newValue.filter((item) => item !== selectedItem['value'] && item !== 'all')
      } else {
        newValue.push(selectedItem['value'])
        if (newValue.length === options.length) {
          newValue = ['all', ...newValue]
        }
      }

      let newText = innerState['text']
      if (innerState['text'].includes(selectedItem['text'])) {
        newText = newText.filter((item) => item !== selectedItem['text'] && item !== 'all')
      } else {
        newText.push(selectedItem['text'])
        if (newValue.includes('all')) {
          newText = ['all', ...newText]
        }
      }

      setInnerState({ value: newValue, text: newText })
    }
  }

  const handleSelectAll = (selectAll: boolean, isDefaultAll?: boolean) => {
    if (selectAll) {
      const allValue = ['all', ...options.map((item) => item.value)]
      const allText = ['all', ...options.map((item) => item.text)]

      setInnerState({
        value: allValue,
        text: allText,
        isDefaultAll,
      })
    } else {
      setInnerState({ value: [], text: [], isDefaultAll })
    }
  }

  const filteredOptions = React.useMemo((): Option[] => {
    // Search text with non alphanumeric characters stripped
    const strippedSearchText = searchText.toLowerCase().replace(/[^0-9a-z]/gi, '')

    let visibleOptions = options.filter((option) => {
      return option.text
        .toLowerCase()
        .replace(/[^0-9a-z]/gi, '')
        .includes(strippedSearchText)
    })
    if (!searchText) visibleOptions.unshift({ text: 'Select All', value: 'all' })

    return visibleOptions
  }, [options, searchText])

  const OptionItem = React.forwardRef(({ item }: { item: Option }, ref) => {
    return (
      <div className={`p-3 border-b border-gray-200 hover:bg-primary-lighter`}>
        <label htmlFor={item.value} className="checkbox-container">
          <input
            className="mx-3"
            type="checkbox"
            id={item.value}
            name={item.value}
            value={item.value}
            checked={isSelected(item)}
            onChange={(event) => {
              handleSelect(event, item)
            }}
          />
          <span className="checkmark"></span>
          <span className="whitespace-pre-wrap">{item.text}</span>
        </label>
      </div>
    )
  })

  return (
    <>
      <div className="flex">
        <div className={`flex gap-2 w-full ${buttonBelow ? 'flex-col items-end sm:w-64' : 'flex-row sm:w-100'}`}>
          <TextBox
            id={id}
            label={label}
            placeholder={placeholder}
            outlined
            disabled={disabled}
            InputProps={{
              readOnly: true,
            }}
            value={
              text.includes('all')
                ? 'All'
                : (options.length > 0
                    ? options.filter((item) => value.includes(item.value)).map((item) => item.text)
                    : text
                  ).join(', ')
            }
            error={error}
            helperText={helperText}
          />
          <div className={`${buttonBelow ? '' : 'mt-11'}`}>
            <SecondaryButton buttonText={buttonText} minWidth="142px" disabled={disabled} onClick={handleOpen} />
          </div>
        </div>
      </div>
      {open && (
        <Modal title={modalTitle} open={open} onClose={handleClose}>
          {isLoading && <Loader variant="inlineFull" />}
          <div className="flex flex-col">
            <div className="flex flex-row justify-between">
              <div className="w-full mb-8 sm:w-64">
                <TextBox id="search-box" placeholder="Search..." outlined value={searchText} onChange={setSearchText} />
              </div>
              <div className="flex">
                <PrimaryButton buttonText="Apply" onClick={handleApply} />
              </div>
            </div>
            <div className="w-full mb-10" style={{ height: 420 }}>
              {filteredOptions.length > 1 || (filteredOptions.length === 1 && filteredOptions[0].value !== 'all') ? (
                <Virtuoso
                  allowFullScreen
                  totalCount={filteredOptions.length}
                  itemContent={(index) => {
                    const item = filteredOptions[index]
                    return <OptionItem key={item.value} item={item} />
                  }}
                />
              ) : (
                <span className="flex justify-center mt-32 text-lg font-semibold">No Data to Display</span>
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}
